import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { ConnectsLayout } from '@/pages/connects/ConnectsLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { PortalLayout } from '@/layouts/PortalLayout'
import { PATHS } from '@/routes/paths'

const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  })),
)
const ContactsPage = lazy(() =>
  import('@/pages/connects/ContactsPage').then((m) => ({
    default: m.ContactsPage,
  })),
)
const TodoListPage = lazy(() =>
  import('@/pages/connects/TodoListPage').then((m) => ({
    default: m.TodoListPage,
  })),
)
const EmailPage = lazy(() =>
  import('@/pages/connects/EmailPage').then((m) => ({ default: m.EmailPage })),
)
const PipelinePage = lazy(() =>
  import('@/pages/connects/PipelinePage').then((m) => ({
    default: m.PipelinePage,
  })),
)
const ActivityPage = lazy(() =>
  import('@/pages/connects/ActivityPage').then((m) => ({
    default: m.ActivityPage,
  })),
)
const ContactDetailPage = lazy(() =>
  import('@/pages/connects/ContactDetailPage').then((m) => ({
    default: m.ContactDetailPage,
  })),
)
const UsersPage = lazy(() =>
  import('@/pages/users/UsersPage').then((m) => ({ default: m.UsersPage })),
)
const PaymentGatewaysPage = lazy(() =>
  import('@/pages/gateways/PaymentGatewaysPage').then((m) => ({
    default: m.PaymentGatewaysPage,
  })),
)
const PlansPage = lazy(() =>
  import('@/pages/plans/PlansPage').then((m) => ({ default: m.PlansPage })),
)
const EmailConfigPage = lazy(() =>
  import('@/pages/email/EmailConfigPage').then((m) => ({
    default: m.EmailConfigPage,
  })),
)
const LoginLogsPage = lazy(() =>
  import('@/pages/logs/LoginLogsPage').then((m) => ({
    default: m.LoginLogsPage,
  })),
)
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const ChangePasswordPage = lazy(() =>
  import('@/pages/profile/ChangePasswordPage').then((m) => ({
    default: m.ChangePasswordPage,
  })),
)
const NotificationsPage = lazy(() =>
  import('@/pages/notifications/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
)

function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

const router = createBrowserRouter([
  { index: true, element: <Navigate to={PATHS.login} replace /> },

  {
    element: <AuthLayout />,
    children: [
      {
        path: PATHS.login,
        element: (
          <Lazy>
            <LoginPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.forgotPassword,
        element: (
          <Lazy>
            <ForgotPasswordPage />
          </Lazy>
        ),
      },
    ],
  },

  {
    element: <PortalLayout />,
    children: [
      {
        path: PATHS.dashboard,
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.connects,
        children: [
          {
            path: 'contacts/:contactId',
            element: (
              <Lazy>
                <ContactDetailPage />
              </Lazy>
            ),
          },
          {
            element: <ConnectsLayout />,
            children: [
              { index: true, element: <Navigate to={PATHS.connectsContacts} replace /> },
              {
                path: 'contacts',
                element: (
                  <Lazy>
                    <ContactsPage />
                  </Lazy>
                ),
              },
              {
                path: 'todo',
                element: (
                  <Lazy>
                    <TodoListPage />
                  </Lazy>
                ),
              },
              {
                path: 'email',
                element: (
                  <Lazy>
                    <EmailPage />
                  </Lazy>
                ),
              },
              {
                path: 'pipeline',
                element: (
                  <Lazy>
                    <PipelinePage />
                  </Lazy>
                ),
              },
              {
                path: 'activity',
                element: (
                  <Lazy>
                    <ActivityPage />
                  </Lazy>
                ),
              },
            ],
          },
        ],
      },
      {
        path: PATHS.users,
        element: (
          <Lazy>
            <UsersPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.paymentGateways,
        element: (
          <Lazy>
            <PaymentGatewaysPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.plans,
        element: (
          <Lazy>
            <PlansPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.emailConfig,
        element: (
          <Lazy>
            <EmailConfigPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.loginLogs,
        element: (
          <Lazy>
            <LoginLogsPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.profile,
        element: (
          <Lazy>
            <ProfilePage />
          </Lazy>
        ),
      },
      {
        path: PATHS.changePassword,
        element: (
          <Lazy>
            <ChangePasswordPage />
          </Lazy>
        ),
      },
      {
        path: PATHS.notifications,
        element: (
          <Lazy>
            <NotificationsPage />
          </Lazy>
        ),
      },
    ],
  },

  { path: '*', element: <Navigate to={PATHS.login} replace /> },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
